import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Divider, Form, FormInstance, Input, Popover } from "antd";
import { DownOutlined, CloseCircleOutlined, RightOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useRequest } from "ahooks";
import { getAllAffiliateMarketersByShop } from "@/services/api/order";
import ShopDTO from "@/dto/shop/ShopDTO";
import Highlight from "@/components/Highlight";
import "./index.less";
import classNames from "classnames";
import { buildAffiliateMarketerKey, parseAffiliateMarketerKey } from "./utils";

interface AffiliateMarketerFieldProps {
    form: FormInstance;
    shops: ShopDTO[];
}

interface AffiliateMarketerOptionItem {
    value: string;
    label: string;
    shopId: number;
    shopName: string;
}

interface AffiliateMarketerCascaderOption {
    value: boolean;
    label: string;
    /** 选中后搜索全部带货达人，不展开达人列表 */
    selectAllMarketers?: boolean;
    children?: AffiliateMarketerOptionItem[];
}

type AffiliateMarketerCascaderValue = [boolean, string[]?];

enum AffiliateMarketerPanel {
    SELF,
    ALL,
    SPECIFIED,
}

function resolveActivePanel(value: AffiliateMarketerCascaderValue, hasAllMarketerOption: boolean) {
    if (!value?.length) return undefined;

    if (value[0] === false) return AffiliateMarketerPanel.SELF;

    if (hasAllMarketerOption && !value[1]?.length) return AffiliateMarketerPanel.ALL;

    return AffiliateMarketerPanel.SPECIFIED;
}

interface AffiliateMarketerCascaderProps {
    options: AffiliateMarketerCascaderOption[];
    value?: AffiliateMarketerCascaderValue;
    onChange?: (value?: AffiliateMarketerCascaderValue) => void;
    placeholder?: string;
    allowClear?: boolean;
    showShopName?: boolean;
    onOpenChange?: (open: boolean) => void;
}

function isString(value?: string): value is string {
    return !!value;
}

const EMPTY_MARKETER_OPTIONS: AffiliateMarketerOptionItem[] = [];
const MARKETER_ROW_HEIGHT = 32;
const MARKETER_OVERSCAN = 8;

type MarketerListRow =
    | { type: "shop"; key: string; shopName: string }
    | { type: "marketer"; key: string; item: AffiliateMarketerOptionItem };

function buildMarketerRows(options: AffiliateMarketerOptionItem[], showShopName?: boolean) {
    const rows: MarketerListRow[] = [];

    for (let index = 0; index < options.length; index += 1) {
        const item = options[index];

        if (showShopName && item.shopId !== options[index - 1]?.shopId) {
            rows.push({
                type: "shop",
                key: `shop-${item.shopId}-${index}`,
                shopName: item.shopName,
            });
        }

        rows.push({
            type: "marketer",
            key: item.value,
            item,
        });
    }

    return rows;
}

function getVisibleMarketerRange(length: number, scrollTop: number, viewportHeight: number) {
    const viewHeight = viewportHeight > 0 ? viewportHeight : MARKETER_ROW_HEIGHT * 10;
    const start = Math.max(0, Math.floor(scrollTop / MARKETER_ROW_HEIGHT) - MARKETER_OVERSCAN);
    const end = Math.min(length, Math.ceil((scrollTop + viewHeight) / MARKETER_ROW_HEIGHT) + MARKETER_OVERSCAN);

    return { start, end };
}

interface AffiliateMarketerOptionListProps {
    rows: MarketerListRow[];
    selectedMarketerIds: string[];
    searchKeyword: string;
    onToggle: (id: string, checked: boolean) => void;
}

/** 只挂载可视区域附近的达人，避免选项过多时打开面板卡死 */
const AffiliateMarketerOptionList: React.FC<AffiliateMarketerOptionListProps> = function (props) {
    const { rows, selectedMarketerIds, searchKeyword, onToggle } = props;
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(0);
    const highlightTexts = useMemo(() => (searchKeyword ? [searchKeyword] : []), [searchKeyword]);
    const range = getVisibleMarketerRange(rows.length, scrollTop, viewportHeight);
    const visibleRows = rows.slice(range.start, range.end);

    useLayoutEffect(() => {
        const el = scrollRef.current;

        if (!el) return;

        const syncHeight = () => {
            setViewportHeight((prev) => (prev === el.clientHeight ? prev : el.clientHeight));
        };

        syncHeight();

        const observer = new ResizeObserver(syncHeight);
        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        const el = scrollRef.current;

        if (el) {
            el.scrollTop = 0;
        }

        setScrollTop(0);
    }, [searchKeyword]);

    function handleScroll(event: React.UIEvent<HTMLDivElement>) {
        const el = event.currentTarget;
        const top = el.scrollTop;
        const height = el.clientHeight;
        const next = getVisibleMarketerRange(rows.length, top, height);
        const current = getVisibleMarketerRange(rows.length, scrollTop, viewportHeight || height);

        if (next.start === current.start && next.end === current.end) {
            if (height !== viewportHeight) {
                setViewportHeight(height);
            }

            return;
        }

        setScrollTop(top);

        if (height !== viewportHeight) {
            setViewportHeight(height);
        }
    }

    return (
        <div className="amc-scroll" ref={scrollRef} onScroll={handleScroll}>
            {rows.length ? (
                <div
                    style={{
                        height: rows.length * MARKETER_ROW_HEIGHT - range.start * MARKETER_ROW_HEIGHT,
                        marginTop: range.start * MARKETER_ROW_HEIGHT,
                    }}
                >
                    {visibleRows.map((row) => {
                        if (row.type === "shop") {
                            return (
                                <div className="amc-shop-name" key={row.key} title={row.shopName}>
                                    {row.shopName}
                                </div>
                            );
                        }

                        const checked = selectedMarketerIds.includes(row.item.value);

                        return (
                            <div
                                key={row.key}
                                title={row.item.label}
                                className={classNames("amc-checkbox-item", { "is-active": checked })}
                            >
                                <Checkbox
                                    className="w"
                                    checked={checked}
                                    onChange={(event) => onToggle(row.item.value, event.target.checked)}
                                >
                                    <Highlight text={row.item.label} highlightTexts={highlightTexts} />
                                </Checkbox>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="amc-empty"></div>
            )}
        </div>
    );
};

const AffiliateMarketerCascader: React.FC<AffiliateMarketerCascaderProps> = function (props) {
    const { options, value, onChange, placeholder, allowClear, showShopName, onOpenChange } = props;
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState("");

    const selfOption = options.find((item) => item.value === false && !item.selectAllMarketers);
    const allMarketerOption = options.find((item) => item.selectAllMarketers);
    const marketerOption = options.find((item) => item.value === true && !item.selectAllMarketers);
    const marketerOptions = marketerOption?.children || EMPTY_MARKETER_OPTIONS;
    const [activePanel, setActivePanel] = useState<AffiliateMarketerPanel | undefined>(() =>
        resolveActivePanel(value, !!allMarketerOption),
    );

    const selectedMarketerIds = useMemo(() => {
        if (!value || value[0] !== true) return [];

        return value[1] || [];
    }, [value]);

    const selectedMarketerNames = useMemo(() => {
        if (!selectedMarketerIds.length) return [];

        const nameMap = new Map(marketerOptions.map((item) => [item.value, item.label]));

        return selectedMarketerIds.map((id) => nameMap.get(id)).filter(isString);
    }, [selectedMarketerIds, marketerOptions]);

    const displayValue = useMemo(() => {
        if (!value?.length) return "";

        if (value[0] === false) {
            return selfOption?.label || "";
        }

        if (!selectedMarketerIds.length) {
            return allMarketerOption?.label || "";
        }

        if (!selectedMarketerNames.length) return "";

        if (selectedMarketerNames.length <= 2) {
            return selectedMarketerNames.join("、");
        }

        return `已选${selectedMarketerNames.length}位达人`;
    }, [value, selfOption?.label, allMarketerOption?.label, selectedMarketerIds.length, selectedMarketerNames]);

    const searchKeyword = searchText.trim();

    const filteredMarketerOptions = useMemo(() => {
        if (!searchKeyword) return marketerOptions;

        return marketerOptions.filter((item) => item.label?.includes(searchKeyword));
    }, [marketerOptions, searchKeyword]);

    const filteredSelectedCount = useMemo(() => {
        return filteredMarketerOptions.filter((item) => selectedMarketerIds.includes(item.value)).length;
    }, [filteredMarketerOptions, selectedMarketerIds]);

    const marketerRows = useMemo(
        () => buildMarketerRows(filteredMarketerOptions, showShopName),
        [filteredMarketerOptions, showShopName],
    );

    const showSelectAll = !!searchKeyword && filteredMarketerOptions.length > 1;
    const isFilteredAllSelected = showSelectAll && filteredSelectedCount === filteredMarketerOptions.length;
    const isFilteredIndeterminate =
        showSelectAll && filteredSelectedCount > 0 && filteredSelectedCount < filteredMarketerOptions.length;

    function handleClear() {
        onChange?.(undefined);
        setSearchText("");
        setOpen(false);
    }

    function handleSelectSelf() {
        onChange?.([false]);
        setActivePanel(AffiliateMarketerPanel.SELF);
        setSearchText("");
        setOpen(false);
    }

    function handleSelectAllMarketers() {
        onChange?.([true]);
        setActivePanel(AffiliateMarketerPanel.ALL);
        setSearchText("");
        setOpen(false);
    }

    function handleSelectMarketer() {
        setActivePanel(AffiliateMarketerPanel.SPECIFIED);
    }

    function handleMarketerChange(values: string[]) {
        if (!values?.length) {
            onChange?.(undefined);
        } else {
            onChange?.([true, values]);
        }
    }

    function handleToggleMarketer(id: string, checked: boolean) {
        if (checked) {
            if (selectedMarketerIds.includes(id)) return;

            handleMarketerChange([...selectedMarketerIds, id]);

            return;
        }

        handleMarketerChange(selectedMarketerIds.filter((item) => item !== id));
    }

    function handleSelectAllFiltered(checked: boolean) {
        const filteredIds = filteredMarketerOptions.map((item) => item.value);

        if (!filteredIds.length) return;

        if (checked) {
            const selectedIdSet = new Set([...selectedMarketerIds, ...filteredIds]);

            handleMarketerChange(Array.from(selectedIdSet));

            return;
        }

        const filteredIdSet = new Set(filteredIds);
        const nextSelectedIds = selectedMarketerIds.filter((id) => !filteredIdSet.has(id));

        handleMarketerChange(nextSelectedIds);
    }

    const content = (
        <div className="affiliate-marketer-cascader">
            <div className="amc-left">
                {options.map((option) => {
                    const panel: AffiliateMarketerPanel = option.selectAllMarketers
                        ? AffiliateMarketerPanel.ALL
                        : option.value === false
                        ? AffiliateMarketerPanel.SELF
                        : AffiliateMarketerPanel.SPECIFIED;

                    return (
                        <div
                            key={`${panel}-${option.label}`}
                            className={activePanel === panel ? "amc-option is-active" : "amc-option"}
                            onClick={() => {
                                if (panel === AffiliateMarketerPanel.SELF) {
                                    handleSelectSelf();
                                    return;
                                }

                                if (panel === AffiliateMarketerPanel.ALL) {
                                    handleSelectAllMarketers();
                                    return;
                                }

                                handleSelectMarketer();
                            }}
                        >
                            {option.label}
                            {panel === AffiliateMarketerPanel.SPECIFIED ? <RightOutlined /> : null}
                        </div>
                    );
                })}
            </div>

            {activePanel !== AffiliateMarketerPanel.SPECIFIED ? null : (
                <div className="amc-right">
                    <>
                        <Input
                            className="amc-search"
                            placeholder="搜索达人"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            size="small"
                        />
                        {showSelectAll ? (
                            <>
                                <div className="amc-select-all">
                                    <Checkbox
                                        checked={isFilteredAllSelected}
                                        indeterminate={isFilteredIndeterminate}
                                        onChange={(event) => handleSelectAllFiltered(event.target.checked)}
                                    >
                                        全选
                                        <span className="amc-count">({filteredMarketerOptions.length})</span>
                                    </Checkbox>
                                </div>
                                <Divider style={{ margin: 0, padding: 0 }} />
                            </>
                        ) : null}
                        <AffiliateMarketerOptionList
                            rows={marketerRows}
                            selectedMarketerIds={selectedMarketerIds}
                            searchKeyword={searchKeyword}
                            onToggle={handleToggleMarketer}
                        />
                    </>
                </div>
            )}
        </div>
    );

    const canClear = allowClear && !!value?.length;

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={(next) => {
                if (next) {
                    setActivePanel(resolveActivePanel(value, !!allMarketerOption));
                }

                setOpen(next);
                onOpenChange?.(next);
            }}
            placement="bottomLeft"
            classNames={{ root: "affiliate-marketer-cascader-popover" }}
            styles={{ content: { padding: 0 } }}
        >
            <Input
                readOnly
                placeholder={placeholder}
                value={displayValue}
                onClick={() => setOpen(true)}
                className="affiliate-marketer-cascader-input"
                suffix={
                    <span className="amc-suffix">
                        {canClear ? (
                            <CloseCircleOutlined
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleClear();
                                }}
                                style={{
                                    position: "absolute",
                                    margin: 0,
                                    top: "50%",
                                    right: "4px",
                                    marginTop: -6,
                                    color: "rgba(0, 0, 0, 0.25)",
                                    fontSize: 12,
                                    height: 12,
                                }}
                            />
                        ) : (
                            <DownOutlined
                                className="amc-down"
                                style={{
                                    position: "absolute",
                                    margin: 0,
                                    top: "50%",
                                    right: "4px",
                                    marginTop: -6,
                                    color: "rgba(0, 0, 0, 0.25)",
                                    fontSize: 12,
                                    height: 12,
                                }}
                            />
                        )}
                    </span>
                }
            />
        </Popover>
    );
};

const AffiliateMarketerField: React.FC<AffiliateMarketerFieldProps> = function (props) {
    const { form, shops } = props;

    const shopIdsKey = useMemo(() => shops?.map((shop) => shop.id).join(",") || "", [shops]);
    const showShopName = shops?.length > 1;

    const { data: affiliateMarketers, runAsync } = useRequest(
        async () => {
            if (!shops?.length) return [];

            const results = await Promise.all(
                shops.map(async (shop) => {
                    const affiliateMarketers = await getAllAffiliateMarketersByShop(shop.id);

                    return affiliateMarketers.map((affiliateMarketer) => ({
                        value: buildAffiliateMarketerKey(shop.id, affiliateMarketer.id),
                        label: affiliateMarketer.name,
                        shopId: shop.id,
                        shopName: shop.name,
                    }));
                }),
            );

            return results.reduce<AffiliateMarketerOptionItem[]>((prev, current) => prev.concat(current), []);
        },
        { refreshDeps: [shopIdsKey], manual: true },
    );

    const options = useMemo<AffiliateMarketerCascaderOption[]>(() => {
        return [
            {
                value: false,
                label: "非带货达人订单",
            },
            {
                value: true,
                label: "所有带货达人订单",
                selectAllMarketers: true,
            },
            {
                value: true,
                label: "指定带货达人订单",
                children: affiliateMarketers,
            },
        ];
    }, [affiliateMarketers]);

    useEffect(() => {
        const fromOrigin = form.getFieldValue("fromOrigin") as AffiliateMarketerCascaderValue;

        if (!fromOrigin || fromOrigin[0] !== true || !fromOrigin[1]?.length) return;

        const shopIdSet = new Set(shops?.map((shop) => shop.id));
        const nextAffiliateMarketerKeys = fromOrigin[1].filter((key) => {
            const [shopId] = parseAffiliateMarketerKey(key);

            return shopIdSet.has(shopId);
        });

        if (nextAffiliateMarketerKeys.length !== fromOrigin[1].length) {
            form.setFieldsValue({
                fromOrigin: nextAffiliateMarketerKeys.length ? [true, nextAffiliateMarketerKeys] : undefined,
            });
        }
    }, [shopIdsKey]);

    return (
        <Form.Item
            label={<span className="search-field-label">订单来源</span>}
            name="fromOrigin"
            className="affiliate-marketer-field"
            preserve={false}
        >
            <AffiliateMarketerCascader
                placeholder="非带货达人/带货达人"
                allowClear
                showShopName={showShopName}
                options={options}
                onOpenChange={(visible) => {
                    if (visible) {
                        runAsync();
                    }
                }}
            />
        </Form.Item>
    );
};

export default observer(AffiliateMarketerField);
